import { STEP_DT } from '../engine/constants';
import type { Level, Pt } from '../engine/level';
import { GameWorld } from '../engine/world';
import { attachInput } from './input';
import { Renderer } from './renderer';
import { fitRoot, watchViewport } from './viewport';

export type PlayEvents = { onCleared(level: Level, stars: number): void; onBack(): void; onNext(level: Level): void; hasNext(level: Level): boolean };

export class PlayScreen {
  private world: GameWorld;
  private renderer: Renderer;
  private root: HTMLElement;
  private detach: () => void; private unwatch: () => void;
  private raf = 0; private acc = 0; private lastT = 0; private shown = 'playing';
  private trail: Pt[] = []; private moved = 0; private startP: Pt | null = null;

  constructor(host: HTMLElement, private level: Level, private ev: PlayEvents) {
    this.world = new GameWorld(level);
    this.root = document.createElement('div'); this.root.className = 'play';
    this.root.innerHTML = `
      <div class="hud top"><button class="icon back" aria-label="목록">‹</button><div class="title"><span class="starbar"></span> <span class="dim">level ${level.n}</span></div><button class="icon retry" aria-label="다시">↻</button></div>
      <canvas class="board"></canvas>
      <div class="hint" hidden></div>
      <div class="overlay" hidden><div class="card"><div class="big">YUM</div><div class="sub"></div><div class="row"><button class="btn again">다시</button><button class="btn next">다음</button></div></div></div>`;
    host.replaceChildren(this.root);
    const canvas = this.root.querySelector<HTMLCanvasElement>('canvas')!;
    this.renderer = new Renderer(canvas);
    this.root.querySelector('.back')!.addEventListener('click', () => ev.onBack());
    this.root.querySelector('.retry')!.addEventListener('click', () => this.restart());
    this.root.querySelector('.again')!.addEventListener('click', () => this.restart());
    const next = this.root.querySelector<HTMLButtonElement>('.next')!;
    next.hidden = !ev.hasNext(level); next.addEventListener('click', () => ev.onNext(level));
    this.detach = attachInput(canvas, (x, y) => this.renderer.toWorld(x, y), {
      onStart: (p) => { this.trail = [p]; this.startP = p; this.moved = 0; },
      onMove: (p) => {
        const last = this.trail[this.trail.length - 1];
        if (last) { this.moved += Math.hypot(p[0] - last[0], p[1] - last[1]); this.world.cut(last, p); }
        this.trail.push(p); if (this.trail.length > 12) this.trail.shift();
      },
      onEnd: () => { if (this.startP && this.moved < 0.15) this.world.tap(this.startP); this.trail = []; this.startP = null; },
    });
    this.unwatch = watchViewport(() => this.fit());
    this.lastT = performance.now();
    const loop = (t: number) => { this.raf = requestAnimationFrame(loop); this.tick(t); };
    this.raf = requestAnimationFrame(loop);
  }
  private fit() { const [w, h] = fitRoot(this.root); this.renderer.canvas.style.top = '44px'; this.renderer.resize(w, Math.max(100, h - 44), window.devicePixelRatio || 1); }
  private tick(t: number) {
    const dt = Math.min((t - this.lastT) / 1000, 4 * STEP_DT); this.lastT = t; this.acc += dt;
    let n = 0; while (this.acc >= STEP_DT && n < 4) { this.world.step(); this.acc -= STEP_DT; n++; }
    const snap = this.world.snapshot();
    this.renderer.draw(this.world, snap, this.trail);
    this.root.querySelector('.starbar')!.textContent = '★'.repeat(snap.stars) + '☆'.repeat(Math.max(0, snap.starsTotal - snap.stars));
    if (snap.status !== this.shown) {
      this.shown = snap.status;
      if (snap.status === 'cleared') { this.root.querySelector('.sub')!.textContent = `${'★'.repeat(snap.stars)}${'☆'.repeat(snap.starsTotal - snap.stars)}`; this.root.querySelector<HTMLElement>('.overlay')!.hidden = false; this.ev.onCleared(this.level, snap.stars); }
      else if (snap.status === 'lost') { const h = this.root.querySelector<HTMLElement>('.hint')!; h.textContent = '놓쳤어요 — ↻ 로 다시'; h.hidden = false; }
    }
  }
  restart() { this.world = new GameWorld(this.level); this.shown = 'playing'; this.acc = 0; this.root.querySelector<HTMLElement>('.overlay')!.hidden = true; this.root.querySelector<HTMLElement>('.hint')!.hidden = true; }
  destroy() { cancelAnimationFrame(this.raf); this.detach(); this.unwatch(); this.root.remove(); }
}
